/**
 * Annie AI Chat API Client
 *
 * Handles communication with the FastAPI backend for AI chat functionality.
 * Uses Server-Sent Events (SSE) for streaming responses.
 */

import { getAuthToken } from 'src/helpers/auth';

const FASTAPI_URL = import.meta.env.VITE_FASTAPI_URL || 'http://localhost:3013';

interface UIActions {
  show_document_ids: string[];
  hide_document_ids: string[];
}

interface ChatResponse {
  status: string;
  ui_actions: UIActions;
  suggestion_group_ids: string[];
}

interface StreamCallbacks {
  onToken?: (token: string) => void;
  onStatus?: (status: string) => void;
  onError?: (error: string) => void;
}

/**
 * Send a message to Annie and get a streaming response
 * @param message - User message
 * @param context - Context object (shownIds, focusedDocumentId, annieContext, organizeContext)
 * @param callbacks - Stream event callbacks
 * @returns Promise resolving to final chat response
 */
export const sendMessage = async (
  message: string,
  callbacks?: StreamCallbacks,
  context?: Record<string, unknown>,
): Promise<ChatResponse> => {
  const token = await getAuthToken();

  // Add timeout handling - fetch doesn't have built-in timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

  try {
    const response = await fetch(`${FASTAPI_URL}/ai/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ message, context: context || {} }),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      let errorMessage = 'An error occurred. Please try again.';
      try {
        const errorText = await response.text();
        // Try to parse as JSON first (FastAPI returns JSON errors)
        try {
          const errorJson = JSON.parse(errorText);
          // FastAPI error format: {"detail": "error message"}
          if (errorJson.detail) {
            // Don't expose raw technical errors to users
            const detail = String(errorJson.detail).toLowerCase();
            if (detail.includes('internal server error') || detail.includes('stack trace')) {
              errorMessage = 'An error occurred. Please try again.';
            } else {
              // Use the detail message if it's user-friendly
              errorMessage = errorJson.detail;
            }
          }
        } catch {
          // Not JSON, use the text if it's reasonable
          if (errorText && !errorText.toLowerCase().includes('internal server error')) {
            errorMessage = errorText;
          }
        }
      } catch {
        // Fallback to generic message
      }
      // Create error with status code for rate limit detection
      const errorObj = new Error(`Chat failed: ${errorMessage}`) as Error & { status?: number };
      errorObj.status = response.status;
      throw errorObj;
    }

    // Handle SSE stream
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let finalResponse: ChatResponse | null = null;
    let buffer = '';
    let errorAlreadyReported = false;

    // Read stream chunks until done
    let streamDone = false;
    while (!streamDone) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) {
          streamDone = true;
          break;
        }

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (let i = 0; i < lines.length; i += 1) {
          const line = lines[i].trim();
          if (line) {
            if (line.startsWith('event: ')) {
              const eventType = line.slice(7);
              const dataLine = lines[i + 1];

              if (dataLine && dataLine.startsWith('data: ')) {
                let data: Record<string, unknown>;
                try {
                  data = JSON.parse(dataLine.slice(6));
                } catch (parseError) {
                  // Backend sent invalid JSON - this is a backend bug, but handle gracefully
                  const errorMsg = parseError instanceof Error ? parseError.message : 'Invalid JSON in SSE stream';
                  errorAlreadyReported = true;
                  callbacks?.onError?.(errorMsg);
                  throw new Error(`Failed to parse SSE data: ${errorMsg}`);
                }

                // Handle parsed events (no try-catch here - let errors propagate normally)
                if (eventType === 'status') {
                  callbacks?.onStatus?.(data.status as string);
                } else if (eventType === 'token') {
                  callbacks?.onToken?.(data.token as string);
                } else if (eventType === 'complete') {
                  finalResponse = data as unknown as ChatResponse;
                } else if (eventType === 'error') {
                  // Sanitize error from SSE stream
                  let errorMsg = data.error as string;
                  const errorLower = String(errorMsg).toLowerCase();
                  if (errorLower.includes('internal server error') || errorLower.includes('stack trace')) {
                    errorMsg = 'An error occurred. Please try again.';
                  }
                  errorAlreadyReported = true;
                  callbacks?.onError?.(errorMsg);
                  throw new Error(errorMsg);
                }

                i += 1; // Skip the data line
              }
            }
          }
        }
      } catch (readError) {
        // Stream was closed abruptly (connection reset, network error, etc.)
        // Only call onError if we haven't already reported an error event
        if (!errorAlreadyReported) {
          const errorMsg = readError instanceof Error ? readError.message : 'Stream connection lost';
          callbacks?.onError?.(errorMsg);
        }
        const errorMsg = readError instanceof Error ? readError.message : 'Stream connection lost';
        throw new Error(`Stream error: ${errorMsg}`);
      }
    }

    if (!finalResponse) {
      // If stream ended without complete event, call onError if available
      callbacks?.onError?.('No final response received from Annie');
      throw new Error('No final response received from Annie');
    }

    return finalResponse;
  } catch (fetchError) {
    clearTimeout(timeoutId);
    // Handle abort/timeout errors
    if (fetchError instanceof Error && fetchError.name === 'AbortError') {
      const timeoutError = new Error('Request timeout - please try again') as Error & { status?: number };
      timeoutError.status = 504;
      throw timeoutError;
    }
    // Handle network errors (connection refused, failed to fetch, etc.)
    if (fetchError instanceof TypeError && fetchError.message.includes('fetch')) {
      const networkError = new Error('Network error - please check your connection and try again') as Error & {
        status?: number;
      };
      networkError.status = 0; // Network errors don't have HTTP status codes
      throw networkError;
    }
    // Re-throw other errors (they should already be formatted)
    throw fetchError;
  }
};
